/* eslint-disable max-lines */
import { useLocations } from '@hakimo-ui/hakimo/data-access';
import { LocationFilters } from '@hakimo-ui/hakimo/types';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { Pagination, Table, TableData } from '@hakimo-ui/hakimo/ui-table';
import {
  eventTracker,
  useCanAddLocation,
  useCanUpdateLocation,
  useDebounce,
  useCanViewLocationDetail,
  useLocalStorage,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { Alert, Button } from '@hakimo-ui/shared/ui-base';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AddUpdateLocation from '../add-update-location/AddUpdateLocation';
import { useOnClickRow } from '../hooks';
import { useLocalDispatch, useLocalState } from '../store/StateProvider';
import {
  createUpdateFiltersAction,
  createUpdatePageAction,
  createUpdateSearchValueAction,
  createUpdateSelectedFilterAction,
} from '../store/action-creators';
import { TableHeader } from './TableHeader';
import FilterPanel from './filter-panel/FilterPanel';
import {
  FilterItem,
  columns,
  getSearchParams,
  getTableData,
  searchFilters,
} from './util';

export function LocationList() {
  const state = useLocalState();
  const dispatch = useLocalDispatch();
  const { filters, page, pageSize, searchValue, selectedFilter } = state;
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const debouncedSearchValue = useDebounce(searchValue, 400);

  const [tableData, setTableData] = useState<TableData>({
    columns,
    rows: [],
  });
  const [total, setTotal] = useState(0);
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [openAddLocation, setOpenAddLocation] = useState(false);
  const canUpdateLocation = useCanUpdateLocation();
  const canAddLocation = useCanAddLocation();
  const canViewLocationDetail = useCanViewLocationDetail();

  const allColumns = useMemo(
    () =>
      canUpdateLocation
        ? columns
        : columns.filter((col) => col.id !== 'actions'),
    [canUpdateLocation]
  );

  const [shownColumns, setShownColumns] = useLocalStorage<string[]>(
    'location-list-shown-columns',
    allColumns.map((col) => col.id)
  );

  const { isLoading, isError, error, data } = useLocations(
    getSearchParams(
      filters,
      page,
      pageSize,
      debouncedSearchValue,
      selectedFilter
    )
  );

  const fromIndex = (page - 1) * pageSize;
  const toIndex = Math.min(page * pageSize, total) - 1;
  const onClickRow = useOnClickRow();

  useEffect(() => {
    for (const filter of searchFilters) {
      const value = urlSearchParams.get(filter.id);
      if (value) {
        dispatch(createUpdateSelectedFilterAction(filter));
        dispatch(createUpdateSearchValueAction(value));
        dispatch(createUpdatePageAction(1));
        break;
      }
    }
  }, []);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (debouncedSearchValue) {
      nextParams.set(selectedFilter.id, debouncedSearchValue);
    }
    if (nextParams.toString() !== urlSearchParams.toString()) {
      setUrlSearchParams(nextParams, { replace: true });
    }
  }, [
    debouncedSearchValue,
    selectedFilter,
    urlSearchParams,
    setUrlSearchParams,
  ]);

  useEffect(() => {
    if (data) {
      const { items, total: t } = data;
      setTableData(
        getTableData(
          items,
          onClickRow,
          allColumns,
          shownColumns || [],
          canViewLocationDetail
        )
      );
      setTotal(t);
    }
  }, [
    data,
    onClickRow,
    searchValue,
    allColumns,
    shownColumns,
    canViewLocationDetail,
  ]);

  const onApplyFilters = (value: LocationFilters) => {
    dispatch(createUpdateFiltersAction(value));
    dispatch(createUpdatePageAction(1));
  };

  const onChangeFilter = (filterValue: FilterItem) => {
    dispatch(createUpdateSelectedFilterAction(filterValue));
    dispatch(createUpdatePageAction(1));
  };

  const onSearchFilter = (value: string) => {
    dispatch(createUpdateSearchValueAction(value));
    dispatch(createUpdatePageAction(1));
  };

  const onChangeShownColumns = (val: string[]) => setShownColumns(val);
  const isDataValid = !!data?.items.length;

  const tableHeader = (
    <TableHeader
      hasCustomFiltersApplied={filters.name.values.length > 0}
      isLoading={isLoading}
      onClickFilter={() => setOpenFilterPanel(true)}
      onSearch={onSearchFilter}
      filterValue={selectedFilter}
      onChangeFilter={onChangeFilter}
      allColumns={allColumns}
      shownColumns={shownColumns || []}
      onChangeShownColumns={onChangeShownColumns}
      filters={filters}
      isDataValid={isDataValid}
      searchValue={searchValue}
    />
  );

  const tableFooter = (
    <div className="dark:bg-dark-bg border-t bg-white p-4 dark:border-gray-800">
      <Pagination
        from={fromIndex + 1}
        to={toIndex + 1}
        pageSize={pageSize}
        total={total}
        disabled={isLoading}
        onChangePage={(val) => dispatch(createUpdatePageAction(val))}
      />
    </div>
  );

  return (
    <Page
      title="Locations"
      titleFontSize="large"
      pageActions={[
        canAddLocation ? (
          <Button
            variant="primary"
            onClick={() => {
              setOpenAddLocation(true);
              eventTracker('open_add_location_modal')();
            }}
            disabled={isLoading}
          >
            Add Location
            <span className="sr-only">Add Location</span>
          </Button>
        ) : null,
      ]}
    >
      {isError && (
        <div className="mb-2">
          <Alert type="error">{error.message}</Alert>
        </div>
      )}
      {openAddLocation && (
        <AddUpdateLocation
          onClose={() => setOpenAddLocation(false)}
          mode="add"
        />
      )}
      <FilterPanel
        open={openFilterPanel}
        filters={filters}
        onApplyFilters={onApplyFilters}
        onClose={() => setOpenFilterPanel(false)}
      />
      <div className="dark:bg-dark-bg -mx-4 flex min-h-0 flex-1 flex-col justify-start overflow-y-hidden rounded-t-xl sm:-mx-6 md:mx-0">
        <Table
          data={tableData}
          header={tableHeader}
          footer={tableFooter}
          loading={isLoading}
        />
      </div>
    </Page>
  );
}

export default withAuthz(withErrorBoundary(LocationList), ['location:view']);
